import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const helpers = createMultiStyleConfigHelpers(['container', 'inner', 'mediaRegion', 'textRegion', 'heading', 'body']);

/**
 * MediaContentSection (Text Image) styles
 *
 * Figma Desktop 11835-54399: row, gap 40px, padding 80 80 120; media 510×390; text region gap 40 (title to body).
 * Figma Mobile 11830-60372: column, padding 56 20, gap 40 (header to image); title/body gap 16; image 280×280.
 * Full-bleed section; inner max-width 1440px centered.
 */
export const mediaContentSectionStyles = helpers.defineMultiStyleConfig({
  baseStyle: ({component}) => {
    const bgHex = normalizeHex(component?.backgroundColour);
    const copyColor = normalizeHex(component?.textColour) ?? '#FFFFFF';

    return {
      container: {
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        boxSizing: 'border-box',
        ...(bgHex && {backgroundColor: bgHex}),
      },
      inner: {
        display: 'flex',
        flexDirection: {base: 'column', md: 'row'},
        alignItems: {base: 'stretch', md: 'center'},
        gap: {base: '40px', md: '40px'},
        padding: {base: '56px 20px', md: '80px 80px 120px'},
        maxWidth: '1440px',
        margin: '0 auto',
        boxSizing: 'border-box',
      },
      mediaRegion: {
        flexShrink: 0,
        alignSelf: {base: 'center', md: 'auto'},
        width: {base: '280px', md: '510px'},
        minWidth: {base: '280px', md: '510px'},
        maxWidth: {base: '100%', md: '510px'},
        height: {base: '280px', md: '390px'},
        minHeight: {base: '280px', md: '390px'},
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        sx: {
          '& > *': {
            width: '100%',
            height: '100%',
            minHeight: 0,
            flex: '1 1 auto',
          },
          '& .component--image': {
            width: '100%',
            height: '100%',
            minHeight: '100%',
            display: 'block',
            '& a, & div': { width: '100%', height: '100%', display: 'block' },
            '& img': {
              width: '100%',
              height: '100%',
              minHeight: '100%',
              objectFit: 'cover',
              display: 'block',
              verticalAlign: 'top',
            },
          },
          '& img': {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            minHeight: '100%',
          },
          '& .component--video': {
            width: '100%',
            height: '100%',
            '& video, & iframe': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
          },
        },
      },
      textRegion: {
        flex: {base: '0 0 auto', md: '1'},
        display: 'flex',
        flexDirection: 'column',
        gap: {base: '16px', md: '40px'},
      },
      heading: {
        fontFamily: 'aktiv-grotesk, sans-serif',
        fontWeight: 500,
        fontSize: {base: '24px', md: '40px'},
        lineHeight: {base: 1.5, md: 1.2},
        letterSpacing: '-0.01em',
        color: copyColor,
      },
      body: {
        fontSize: {base: '16px', md: '16px'},
        lineHeight: 1.4,
        color: copyColor,
      },
    };
  },
  variants: {
    left: () => ({
      inner: {
        flexDirection: {base: 'column', md: 'row'},
      },
      textRegion: {
        order: {base: 1, md: 2},
      },
      mediaRegion: {
        order: {base: 2, md: 1},
      },
    }),
    // Right alignment: JSX is text then media; use row so media stays on the right (not row-reverse).
    right: () => ({
      inner: {
        flexDirection: {base: 'column', md: 'row'},
      },
      textRegion: {
        order: {base: 1, md: 1},
      },
      mediaRegion: {
        order: {base: 2, md: 2},
      },
    }),
  },
  defaultProps: {
    variant: 'left',
  },
});
